import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, TextField,
  Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
]

interface ClassOption {
  class_id: string
  name:     string
}

interface Props {
  open:        boolean
  onClose:     () => void
  onEnrolled:  () => void
}

export default function EnrollDialog({ open, onClose, onEnrolled }: Props) {
  const [classes, setClasses] = useState<ClassOption[]>([])
  const [sFirst,  setSFirst]  = useState('')
  const [sLast,   setSLast]   = useState('')
  const [sEmail,  setSEmail]  = useState('')
  const [sPhone,  setSPhone]  = useState('')
  const [pFirst,  setPFirst]  = useState('')
  const [pLast,   setPLast]   = useState('')
  const [pEmail,  setPEmail]  = useState('')
  const [pPhone,  setPPhone]  = useState('')
  const [grade,   setGrade]   = useState('')
  const [school,  setSchool]  = useState('')
  const [city,    setCity]    = useState('')
  const [stateCode, setStateCode] = useState('')
  const [program, setProgram] = useState('ardent_enrolled')
  const [classId, setClassId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSFirst(''); setSLast(''); setSEmail(''); setSPhone('')
    setPFirst(''); setPLast(''); setPEmail(''); setPPhone('')
    setGrade(''); setSchool(''); setCity(''); setStateCode('')
    setProgram('ardent_enrolled'); setClassId(''); setError(null)
    supabase
      .from('class')
      .select('class_id, name')
      .in('status', ['upcoming', 'active'])
      .order('name')
      .then(({ data }) => setClasses(data ?? []))
  }, [open])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const gradeNum = parseInt(grade, 10)
    if (!sFirst.trim() || !sLast.trim() || !sEmail.trim()) {
      setError('Student first name, last name, and email are required.'); return
    }
    if (!pFirst.trim() || !pLast.trim() || !pEmail.trim()) {
      setError('Parent first name, last name, and email are required.'); return
    }
    if (!pPhone.trim()) { setError('Parent phone number is required.'); return }
    if (!gradeNum || gradeNum < 1 || gradeNum > 12) {
      setError('Grade must be between 1 and 12.'); return
    }
    if (!school.trim() || !stateCode) {
      setError('School name and state are required.'); return
    }

    setLoading(true)
    const { data, error: invokeErr } = await supabase.functions.invoke('enroll-student', {
      body: {
        student_first_name: sFirst.trim(),
        student_last_name:  sLast.trim(),
        student_email:      sEmail.trim().toLowerCase(),
        student_phone:      sPhone.trim() || undefined,
        parent_first_name:  pFirst.trim(),
        parent_last_name:   pLast.trim(),
        parent_email:       pEmail.trim().toLowerCase(),
        parent_phone:       pPhone.trim(),
        grade:              gradeNum,
        school_name:        school.trim(),
        school_city:        city.trim(),
        school_state:       stateCode,
        program_type:       program,
        class_id:           classId || undefined,
      },
    })
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
      setLoading(false); return
    }
    if (data?.error) {
      setError(data.error)
      setLoading(false); return
    }
    setLoading(false)
    onEnrolled()
    onClose()
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Enroll a Student</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accounts will be created with default passwords ('student' and 'parent').
          A welcome email is sent to each — they'll be asked to set a new password on first sign-in.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Student</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField label="First name" size="small" fullWidth value={sFirst} onChange={e => setSFirst(e.target.value)} />
              <TextField label="Last name"  size="small" fullWidth value={sLast}  onChange={e => setSLast(e.target.value)} />
            </Box>
            <TextField label="Student email" size="small" type="email" fullWidth value={sEmail} onChange={e => setSEmail(e.target.value)} sx={{ mb: 2 }} />
            <TextField label="Student phone (optional)" size="small" type="tel" fullWidth value={sPhone} onChange={e => setSPhone(e.target.value)} />
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Parent / Guardian</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField label="First name" size="small" fullWidth value={pFirst} onChange={e => setPFirst(e.target.value)} />
              <TextField label="Last name"  size="small" fullWidth value={pLast}  onChange={e => setPLast(e.target.value)} />
            </Box>
            <TextField label="Parent email" size="small" type="email" fullWidth value={pEmail} onChange={e => setPEmail(e.target.value)} sx={{ mb: 2 }} />
            <TextField label="Parent phone" size="small" type="tel" fullWidth value={pPhone} onChange={e => setPPhone(e.target.value)} />
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>School &amp; Program</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Grade</InputLabel>
                <Select label="Grade" value={grade} onChange={e => setGrade(e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(g => (
                    <MenuItem key={g} value={String(g)}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="School name" size="small" fullWidth value={school} onChange={e => setSchool(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField label="City" size="small" fullWidth value={city} onChange={e => setCity(e.target.value)} />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>State</InputLabel>
                <Select label="State" value={stateCode} onChange={e => setStateCode(e.target.value)}>
                  {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Program type</InputLabel>
                <Select label="Program type" value={program} onChange={e => setProgram(e.target.value)}>
                  <MenuItem value="ardent_enrolled">Ardent Enrolled</MenuItem>
                  <MenuItem value="self_directed">Self Directed</MenuItem>
                </Select>
              </FormControl>
              {classes.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Class (optional)</InputLabel>
                  <Select label="Class (optional)" value={classId} onChange={e => setClassId(e.target.value)}>
                    <MenuItem value="">No class</MenuItem>
                    {classes.map(c => (
                      <MenuItem key={c.class_id} value={c.class_id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Enroll & Send Invitations'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
